/**
 * Session-level funnel instrumentation for the scoring loop.
 *
 * PRD §13 is unusually specific about why this exists: *"The category leader had to retrofit
 * behaviour analytics to discover where users were getting stuck — we ship with session-level
 * funnel instrumentation on the scoring loop from the first build."* #16 makes it an acceptance
 * criterion that this is **live before the field test**, not added afterwards, because the
 * findings a field test produces are unrepeatable — you get one afternoon with that scorer, and
 * a stopwatch and a memory are not evidence.
 *
 * Third of three, and deliberately the same shape as the two that already exist: sign-in
 * friction (`../auth/friction.ts`) and setup timing (`./setupClock.ts`). Pure module, a `Store`
 * it is handed, a `now()` it is handed, one key, no network. It is not a fourth pattern.
 *
 * **No analytics endpoint, on purpose.** PRD FN-3 forbids third-party tracking on this product
 * and CI fails the build on any tracking origin, so there is nothing to send to. The record
 * lives in `localStorage` on the scorer's own device and is read back off it afterwards. For a
 * field test of one match that is not a limitation, it is the whole design: no consent
 * question, no vendor, nothing leaving the phone, and the evidence still survives the walk back
 * to the car.
 *
 * ## The measurement that took a rethink
 *
 * The obvious way to find "where the scorer's thumb hesitated" is to time the gap between one
 * ball and the next. That number is nearly worthless: a real over has thirty to forty seconds
 * between deliveries while the bowler walks back, so inter-ball wall-clock measures **cricket**,
 * not the interface, and the interface is what we can change.
 *
 * So what is timed here is the **entry span** — from the scorer's first touch on the pad for a
 * ball to the moment that ball commits. A plain single-tap four is a span of ~0ms by
 * construction. `WD → 2` or a wicket sheet is a span of however long the scorer spent deciding
 * which control to hit. That difference is entirely ours, contains no walking-back, and is the
 * only part of the loop a redesign could actually shorten.
 */
import type { SyncState } from "@sportsos/sync";
import type { Store } from "../localStore.ts";

/**
 * Keyed per match rather than one global key, unlike the other two modules.
 *
 * `corrections per match` is the ergonomics proxy the ticket names, so a second match must not
 * overwrite the first one's evidence before anybody has read it. Safe to key this way only
 * because `Store` has no way to enumerate keys and every caller already holds the match id — a
 * reader always knows which key to ask for.
 */
export const scoringKey = (matchId: string): string => `sportsos.scoring.${matchId}`;

/**
 * An entry span longer than this counts as a hesitation.
 *
 * A heuristic, and labelled as one: five seconds inside a single ball's entry is long enough
 * that the scorer was hunting for a control rather than using one. The first field test is what
 * calibrates it — if half the balls in a real innings trip it, the threshold is wrong and not
 * the pad.
 */
export const HESITATION_MS = 5_000;

/** Every way a scorer un-does or re-does something. The ergonomics proxy (PRD §13). */
export type CorrectionKind =
  | "runs"
  | "void"
  | "swap_ends"
  | "reassign_over"
  | "substitute"
  | "direction"
  | "undo";

export interface ScoringRecord {
  readonly matchId: string;
  readonly openedAt: number;
  readonly firstBallAt: number | null;
  /**
   * The first touch since the last commit — the open end of the current entry span, or null
   * when the scorer is between balls and has touched nothing.
   */
  readonly touchedAt: number | null;
  readonly balls: number;
  /** One entry span per ball, in ms. See the note at the top of the file. */
  readonly spans: readonly number[];
  readonly corrections: Readonly<Record<string, number>>;
  /**
   * What the scorer did with the wagon wheel (ADR-0022): shots placed, and balls where it asked
   * and was skipped. It is how a field test learns whether the wheel is used or merely tolerated,
   * and a high skip share is the signal that it is slowing the scorer down.
   */
  readonly wagonPlaced: number;
  readonly wagonSkipped: number;
  /** The sync state the pill is showing, and since when, so time can be attributed forward. */
  readonly syncKind: string;
  readonly syncSince: number;
  /** Total ms the pill has shown anything other than settled. */
  readonly unsyncedMs: number;
  readonly longestUnsyncedMs: number;
  readonly maxPending: number;
  readonly everBlocked: boolean;
}

export interface ScoringReport {
  readonly matchId: string;
  readonly balls: number;
  readonly corrections: number;
  readonly correctionsByKind: Readonly<Record<string, number>>;
  readonly wagonPlaced: number;
  readonly wagonSkipped: number;
  /** The comparable figure across matches of different lengths. Null before the first ball. */
  readonly correctionsPerHundredBalls: number | null;
  readonly secondsToFirstBall: number | null;
  readonly medianEntryMs: number | null;
  readonly p90EntryMs: number | null;
  /** Balls whose entry span exceeded `HESITATION_MS`. */
  readonly hesitations: number;
  readonly queuedSeconds: number;
  readonly longestQueuedSeconds: number;
  readonly maxPending: number;
  readonly everBlocked: boolean;
}

/**
 * Whether this state means the queue is settled.
 *
 * `idle` is nothing written yet and `synced` is everything acked; both are "nothing wrong".
 * Every other state — including `syncing` — means a ball is on the device and not yet on the
 * server, which is exactly the window the field test is asked to measure the length of.
 */
const settled = (kind: string): boolean => kind === "idle" || kind === "synced";

const pendingOf = (state: SyncState): number => ("pending" in state ? state.pending : 0);

function percentile(sorted: readonly number[], fraction: number): number | null {
  if (sorted.length === 0) return null;
  // Nearest-rank on a sorted copy. No interpolation: these are millisecond counts read by a
  // human, and an invented value between two real samples would not tell them anything more.
  const rank = Math.ceil(fraction * sorted.length) - 1;
  return sorted[Math.min(Math.max(rank, 0), sorted.length - 1)] ?? null;
}

export function scoringReport(record: ScoringRecord): ScoringReport {
  const spans = [...record.spans].sort((a, b) => a - b);
  const corrections = Object.values(record.corrections).reduce((sum, n) => sum + n, 0);

  return {
    matchId: record.matchId,
    balls: record.balls,
    corrections,
    correctionsByKind: record.corrections,
    wagonPlaced: record.wagonPlaced,
    wagonSkipped: record.wagonSkipped,
    correctionsPerHundredBalls:
      record.balls === 0 ? null : Math.round((corrections / record.balls) * 100),
    secondsToFirstBall:
      record.firstBallAt === null
        ? null
        : Math.round((record.firstBallAt - record.openedAt) / 1000),
    medianEntryMs: percentile(spans, 0.5),
    p90EntryMs: percentile(spans, 0.9),
    hesitations: record.spans.filter((ms) => ms > HESITATION_MS).length,
    queuedSeconds: Math.round(record.unsyncedMs / 1000),
    longestQueuedSeconds: Math.round(record.longestUnsyncedMs / 1000),
    maxPending: record.maxPending,
    everBlocked: record.everBlocked,
  };
}

/**
 * The report as one line a human can read off a phone at the ground.
 *
 * Which is the whole reason it exists. The record lives in `localStorage` on the *scorer's*
 * device, and at a cricket ground there is no devtools console to read it from and nobody is
 * going to plug a stranger's phone into a laptop. So the numbers get rendered on the
 * match-complete panel, where a screenshot captures them and the walk back to the car is
 * enough to collect the finding.
 */
export function formatScoringReport(report: ScoringReport): string {
  const parts = [
    `${report.balls} balls`,
    report.secondsToFirstBall === null ? null : `first ball ${report.secondsToFirstBall}s`,
    `${report.corrections} corrections`,
    // Said only when the wheel was used, so a match scored without it reads as it always did.
    report.wagonPlaced + report.wagonSkipped === 0
      ? null
      : `wheel ${report.wagonPlaced} placed, ${report.wagonSkipped} skipped`,
    report.correctionsPerHundredBalls === null
      ? null
      : `${report.correctionsPerHundredBalls}/100 balls`,
    report.medianEntryMs === null
      ? null
      : `entry p50 ${report.medianEntryMs}ms, p90 ${report.p90EntryMs}ms`,
    `${report.hesitations} hesitations`,
    // Always stated, including when it is zero: "the queue never stalled" is a finding, and a
    // line that omitted it would be indistinguishable from one that forgot to measure.
    `queued ${report.queuedSeconds}s (longest ${report.longestQueuedSeconds}s, peak ${report.maxPending})`,
    report.everBlocked ? "SOME BALLS WERE BLOCKED" : null,
  ];
  return parts.filter((part): part is string => part !== null).join(" · ");
}

export interface ScoringObserver {
  /**
   * The scorer touched the tap half. Opens an entry span; a second call inside the same span
   * does nothing, so the span measures from the *first* touch.
   */
  touched(): void;
  /** A delivery committed. Closes the entry span. */
  ball(): void;
  corrected(kind: CorrectionKind): void;
  /** The wheel asked about a ball and the scorer placed it, or skipped. */
  wagon(outcome: "placed" | "skipped"): void;
  /** The pill changed. Attributes the elapsed time to the state being left. */
  sync(state: SyncState): void;
  report(): ScoringReport | null;
}

export function scoringObserver(
  store: Store,
  matchId: string,
  now: () => number,
): ScoringObserver {
  const key = scoringKey(matchId);

  const blank = (at: number): ScoringRecord => ({
    matchId,
    openedAt: at,
    firstBallAt: null,
    touchedAt: null,
    balls: 0,
    spans: [],
    corrections: {},
    wagonPlaced: 0,
    wagonSkipped: 0,
    syncKind: "idle",
    syncSince: at,
    unsyncedMs: 0,
    longestUnsyncedMs: 0,
    maxPending: 0,
    everBlocked: false,
  });

  const read = (): ScoringRecord | null => {
    try {
      const raw = store.getItem(key);
      if (raw === null || raw === "") return null;
      const parsed = JSON.parse(raw) as Partial<ScoringRecord>;
      // A shape check rather than a cast, because this crosses a reload and a deploy: a record
      // written by an older build is the normal case, not the exceptional one, and one missing
      // field must not throw inside a tap handler on the scoring screen.
      if (typeof parsed.openedAt !== "number") return null;
      return { ...blank(parsed.openedAt), ...parsed, matchId };
    } catch {
      // Unparseable or unreadable. Treated as absent; the next write replaces it.
      return null;
    }
  };

  const write = (record: ScoringRecord): void => {
    try {
      store.setItem(key, JSON.stringify(record));
    } catch {
      // Private browsing and quota. Losing a measurement must never cost a ball.
    }
  };

  /** Read, change, write. Creates the record on first use. */
  const update = (change: (record: ScoringRecord, at: number) => ScoringRecord): void => {
    const at = now();
    write(change(read() ?? blank(at), at));
  };

  /**
   * Close the open sync interval at `at`, accruing it if it was an un-synced one.
   *
   * Split out because both `sync()` and `report()` need it: a queue that is *still* stuck when
   * the report is taken has accrued nothing yet, and a report that ignored the open interval
   * would say the longest stall was zero seconds while the pill sat there saying otherwise.
   */
  const closeInterval = (record: ScoringRecord, at: number): ScoringRecord => {
    if (settled(record.syncKind)) return { ...record, syncSince: at };
    const elapsed = Math.max(0, at - record.syncSince);
    return {
      ...record,
      syncSince: at,
      unsyncedMs: record.unsyncedMs + elapsed,
      longestUnsyncedMs: Math.max(record.longestUnsyncedMs, elapsed),
    };
  };

  // The clock restarts at construction rather than carrying `syncSince` across the reload.
  //
  // The question this answers is "did the pill ever show a stuck queue, and for how long" — a
  // question about what a human saw. A tab closed overnight with two balls queued was not
  // showing anybody anything, and counting those eleven hours would drown the one real
  // forty-second stall that the field test is actually looking for.
  update((record, at) => ({ ...record, syncSince: at }));

  return {
    touched: () =>
      update((record, at) => (record.touchedAt === null ? { ...record, touchedAt: at } : record)),

    ball: () =>
      update((record, at) => ({
        ...record,
        balls: record.balls + 1,
        firstBallAt: record.firstBallAt ?? at,
        touchedAt: null,
        // A commit with no recorded touch is a ball this module did not see the start of — a
        // seeded opening event, or a build where a new control forgot to call `touched()`.
        // Recording a span of `at - openedAt` there would be a fabrication, so it records none.
        spans: record.touchedAt === null ? record.spans : [...record.spans, at - record.touchedAt],
      })),

    corrected: (kind) =>
      update((record) => ({
        ...record,
        corrections: { ...record.corrections, [kind]: (record.corrections[kind] ?? 0) + 1 },
        // A correction ends whatever entry span was open. It is not a ball, so it contributes
        // no span of its own — the deliberation it measures belongs to the ball being fixed.
        touchedAt: null,
      })),

    wagon: (outcome) =>
      update((record) =>
        outcome === "placed"
          ? { ...record, wagonPlaced: record.wagonPlaced + 1 }
          : { ...record, wagonSkipped: record.wagonSkipped + 1 },
      ),

    sync: (state) =>
      update((record, at) => {
        const pending = pendingOf(state);
        const closed = closeInterval(record, at);
        return {
          ...closed,
          syncKind: state.kind,
          maxPending: Math.max(closed.maxPending, pending),
          everBlocked: closed.everBlocked || state.kind === "blocked",
        };
      }),

    report: () => {
      const record = read();
      if (record === null) return null;
      // Against a copy: taking a report is a read, and a scorer who opens the report twice
      // must not see the second one claim the queue recovered because the first one closed
      // the interval.
      return scoringReport(closeInterval(record, now()));
    },
  };
}
